import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import orderBy from 'lodash/orderBy';
import { useAppSelector } from 'src/store';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import DataTable from 'src/shared/components/data-table/DataTable';
import {
    DataTableColumns,
    DataTableState,
    RowsPerPage,
    SortingDirection,
    TableCellData,
    TableCellRendererParams,
    TableCellStructuredData,
    TableRowsSortingFunction,
} from 'src/shared/components/data-table/dataTableTypes';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import { getSelectedMetric } from 'src/app/portfolio/views/portfolio-site-explorer/state/portfolioSiteExplorerSelector';
import { SortOrder } from 'src/shared/types';
import { HabitatInsightsMetricType, habitatInsightsMetricTypeLabel } from 'src/shared/types/graph/survey';
import { formatTableNumber, getPortfolioMetricValue } from 'src/app/portfolio/helpers/portfolioHelpers';
import { habitatInsightsMetricExplainerSlugs } from 'src/shared/constants/habitatInsights';
import usePortfolioSiteTable from '../../hooks/usePortfolioSiteTable';
import usePortfolioSiteExplorerAnalytics from '../../hooks/usePortfolioSiteExplorerAnalytics';
import styles from '../../PortfolioSiteExplorer.module.scss';
import ViewButton from '../view-button/ViewButton';
import SiteSpeciesRiskModal from '../site-species-risk-modal/SiteSpeciesRiskModal';

type PredictedSpeciesColumnMetadata = { count: number; siteId: string };

const ROWS_PER_PAGE = 20 as RowsPerPage;

/** Maps DataTable columnId to the backend sort field name. */
const columnIdToSortField: Record<string, string> = {
    siteName: 'name',
    area: 'area',
    country: 'country',
    [HabitatInsightsMetricType.LAND_COVER]: HabitatInsightsMetricType.LAND_COVER,
    [HabitatInsightsMetricType.PRIMARY_PRODUCTIVITY]: HabitatInsightsMetricType.PRIMARY_PRODUCTIVITY,
    [HabitatInsightsMetricType.ALL_CONNECTIVITY]: HabitatInsightsMetricType.ALL_CONNECTIVITY,
    [HabitatInsightsMetricType.BIODIVERSITY_INTACTNESS_INDEX]: HabitatInsightsMetricType.BIODIVERSITY_INTACTNESS_INDEX,
    [HabitatInsightsMetricType.HUMAN_MODIFICATION_INDEX]: HabitatInsightsMetricType.HUMAN_MODIFICATION_INDEX,
    // Core joins the grid's counts before it sorts and pages, so these sort across the
    // whole portfolio.
    predictedRedList: 'speciesRedListCount',
    predictedInvasive: 'speciesInvasiveCount',
};

const toApiSortOrder = (dir: SortingDirection): 'ASC' | 'DESC' => (dir === SortingDirection.DESC ? 'DESC' : 'ASC');

/** Habitat / insight columns shown on the list tab (same signals as the map). */
const LIST_TAB_HABITAT_METRIC_COLUMNS: HabitatInsightsMetricType[] = [
    HabitatInsightsMetricType.LAND_COVER,
    HabitatInsightsMetricType.PRIMARY_PRODUCTIVITY,
    HabitatInsightsMetricType.ALL_CONNECTIVITY,
    HabitatInsightsMetricType.BIODIVERSITY_INTACTNESS_INDEX,
    HabitatInsightsMetricType.HUMAN_MODIFICATION_INDEX,
];

const listTabMetricColumnLabel = (metric: HabitatInsightsMetricType): string => {
    if (metric === HabitatInsightsMetricType.LAND_COVER) {
        return 'Combined vegetation cover';
    }
    return habitatInsightsMetricTypeLabel[metric] ?? metric;
};

const NON_SORTABLE_COLUMN_IDS = new Set(['predictedRedList', 'predictedInvasive']);

const speciesSortFn: TableRowsSortingFunction = (rows, columnIndex, direction) => {
    const dir = direction === SortingDirection.DESC ? 'desc' : 'asc';
    return orderBy(
        rows,
        [
            row => {
                const cell = row.cells[columnIndex];
                if (cell && typeof cell === 'object' && !React.isValidElement(cell) && 'data' in cell) {
                    return (cell.data as PredictedSpeciesColumnMetadata).count;
                }
                return 0;
            },
        ],
        [dir]
    );
};

const mapMetricToColumnId = (metric: string): string => {
    switch (metric) {
        case 'POSSIBLE_IUCN_RED_LIST_SPECIES':
            return 'predictedRedList';
        case 'POSSIBLE_INVASIVE_SPECIES':
            return 'predictedInvasive';
        case HabitatInsightsMetricType.VEGETATION_CONDITION:
            return HabitatInsightsMetricType.PRIMARY_PRODUCTIVITY;
        default:
            return metric;
    }
};

const LIST_HEADER_TITLE_CLASS = 'flex items-center gap-2 h-[30px] max-h-[30px]';

/** Numeric cells share one renderer: the cell keeps the raw number so the grid
 *  sorts on it, and only the display is rounded. They differ by styling alone —
 *  habitat metrics sit in a fixed-width chip, the area reads as plain text. */
const HABITAT_METRIC_CELL_CLASS = 'inline-block min-w-[2.75rem] px-2 py-1 text-sm tabular-nums';
const AREA_CELL_CLASS = 'tabular-nums';

const numericCellRenderer = (className: string) => (params: TableCellRendererParams) => {
    const display = formatTableNumber(params.cellData as number);
    return display === null ? null : <span className={className}>{display}</span>;
};

/** CMS slugs for QuestionButtonHelp in the table header (question icon, not hover-on-title). */
const getListViewColumnHelpSlugs = (metric: string): { slug: string; detailedSlug: string } | null => {
    if (habitatInsightsMetricExplainerSlugs[metric]) {
        if (metric === HabitatInsightsMetricType.PRIMARY_PRODUCTIVITY) {
            return { slug: 'portfolio-vegetation-health', detailedSlug: 'portfolio-vegetation-health-te' };
        }
        if (metric === HabitatInsightsMetricType.LAND_COVER) {
            return { slug: 'portfolio-combined-vegetated-area', detailedSlug: 'portfolio-combined-vegetated-area-te' };
        }
        const entry = habitatInsightsMetricExplainerSlugs[metric];
        return { slug: entry.defaultSlug, detailedSlug: entry.explainerSlug };
    }
    if (metric === 'POSSIBLE_IUCN_RED_LIST_SPECIES') {
        return { slug: 'portfolio-iucn-red-list-species', detailedSlug: 'portfolio-iucn-red-list-species-te' };
    }
    if (metric === 'POSSIBLE_INVASIVE_SPECIES') {
        return { slug: 'portfolio-invasive-species', detailedSlug: 'portfolio-invasive-species-te' };
    }
    return null;
};

export const PortfolioSiteExplorerListView = () => {
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);
    const selectedMetric = useAppSelector(getSelectedMetric);
    const { currentCustomerId } = useCustomer();
    const { trackDetailedExplainerOpen, trackColumnSortClick, trackSpeciesRiskModalOpen } = usePortfolioSiteExplorerAnalytics();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isModalOpen, setModalOpen] = useState(false);
    const [modalTab, setModalTab] = useState<'iucn' | 'invasives'>('iucn');
    const [modalSiteId, setModalSiteId] = useState<string | null>(null);

    const selectedMetricParam = searchParams.get('selectedMetric');
    const sortDirectionParam = searchParams.get('sortDirection');
    const selectedMetricFromUrlParam = selectedMetricParam as HabitatInsightsMetricType | null;
    const sortDirection = sortDirectionParam as SortOrder | null;

    const getSortColumnId = (isServerSideSort = true): string => {
        if (selectedMetricFromUrlParam) {
            return selectedMetricFromUrlParam;
        }
        if (selectedMetric && selectedMetric !== 'NA') {
            const columnId = mapMetricToColumnId(selectedMetric);
            if (isServerSideSort && NON_SORTABLE_COLUMN_IDS.has(columnId)) {
                return HabitatInsightsMetricType.LAND_COVER;
            }
            return columnId;
        }
        return HabitatInsightsMetricType.LAND_COVER;
    };

    const initialSortColumnId = getSortColumnId();
    const initialSortDirection = sortDirection === SortOrder.DESC ? SortingDirection.DESC : SortingDirection.ASC;

    const [sortState, setSortState] = useState<{ field: string; order: 'ASC' | 'DESC' }>({
        field: columnIdToSortField[initialSortColumnId] ?? initialSortColumnId,
        order: toApiSortOrder(initialSortDirection),
    });

    const initialSorting = {
        sortedColumn: {
            columnId: initialSortColumnId,
            sortedDirection: initialSortDirection,
        },
    };

    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
        rowsPerPage: ROWS_PER_PAGE,
        ...initialSorting,
    });

    useEffect(() => {
        if (selectedMetricFromUrlParam && sortDirection) {
            const clearParamsTimeout = setTimeout(() => {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('selectedMetric');
                newSearchParams.delete('sortDirection');
                newSearchParams.delete('view');
                setSearchParams(newSearchParams, { replace: true });
            }, 100);
            return () => clearTimeout(clearParamsTimeout);
        }
    }, [selectedMetricFromUrlParam, sortDirection, searchParams, setSearchParams]);

    useEffect(() => {
        if (!selectedMetric || selectedMetric === 'NA') return;
        if (selectedMetricFromUrlParam) return;
        const nextColumnId = mapMetricToColumnId(selectedMetric);
        if (NON_SORTABLE_COLUMN_IDS.has(nextColumnId)) return;
        setTableState(prev => ({
            ...prev,
            sortedColumn: {
                columnId: nextColumnId,
                sortedDirection:
                    prev.sortedColumn && prev.sortedColumn.columnId === nextColumnId
                        ? prev.sortedColumn.sortedDirection || SortingDirection.ASC
                        : SortingDirection.ASC,
            },
        }));
    }, [selectedMetric]);

    const {
        rows: siteTableRows,
        totalCount: siteTableTotal,
        currentPage,
        isFetching,
        isServerSideSort,
        goToPage,
    } = usePortfolioSiteTable({
        customerId: currentCustomerId,
        ...(selectedFilters.length ? { filters: selectedFilters } : {}),
        pageSize: ROWS_PER_PAGE,
        sortBy: sortState,
    });

    useEffect(() => {
        if (isServerSideSort) return;
        const columnId = getSortColumnId(false);
        setSortState({ field: columnIdToSortField[columnId] ?? columnId, order: toApiSortOrder(initialSortDirection) });
        setTableState(prev => ({
            ...prev,
            sortedColumn: { columnId, sortedDirection: initialSortDirection },
        }));
        // initialSortDirection is derived from URL params and won't change after mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isServerSideSort]);

    useEffect(() => {
        if (!siteTableRows.length && !isFetching) {
            setTableState(prev => ({ ...prev, data: [] }));
            return;
        }

        const tableData: DataTableState['data'] = siteTableRows.map(row => {
            const structuredRedList: TableCellStructuredData = { data: { count: row.speciesRedListCount ?? 0, siteId: row.id } };
            const structuredInvasive: TableCellStructuredData = { data: { count: row.speciesInvasiveCount ?? 0, siteId: row.id } };
            const siteMetrics = row.metrics ?? [];
            const redListCount = row.speciesRedListCount ?? undefined;
            const invasiveCount = row.speciesInvasiveCount ?? undefined;

            const habitatCells = LIST_TAB_HABITAT_METRIC_COLUMNS.map(metricKey =>
                getPortfolioMetricValue({
                    siteMetrics,
                    metric: metricKey,
                    redList: redListCount,
                    invasive: invasiveCount,
                })
            );

            return [
                row.name,
                row.area,
                row.country,
                ...habitatCells,
                structuredRedList,
                structuredInvasive,
                <ViewButton
                    site={{ id: row.id, hasEdna: row.hasEdna, hasHabitatInsights: row.hasHabitatInsights }}
                    key={row.id}
                    locationForTracking='listview'
                />,
            ];
        });

        setTableState(prev => ({
            ...prev,
            data: tableData,
            rowsPerPage: ROWS_PER_PAGE,
            currentPageNumber: currentPage,
        }));
    }, [siteTableRows, currentPage]);

    const handleOpenModal = (payload: { tab: 'iucn' | 'invasives'; siteId: string }) => {
        setModalTab(payload.tab);
        setModalSiteId(payload.siteId);
        setModalOpen(true);
        const speciesType = payload.tab === 'invasives' ? 'invasive' : payload.tab;
        trackSpeciesRiskModalOpen(payload.siteId, speciesType, 'listview');
    };

    const totalItems = siteTableTotal;
    const displayedSitesCount = !isServerSideSort ? siteTableTotal : siteTableRows.length;

    const columns: DataTableColumns = useMemo(() => {
        const listHeaderTitle = (label: string) => <div className={LIST_HEADER_TITLE_CLASS}>{label}</div>;

        const columnWithOptionalHeaderHelp = (metric: string, label: string) => {
            const help = getListViewColumnHelpSlugs(metric);
            return {
                title: listHeaderTitle(label),
                ...(help
                    ? {
                          titleSlug: help.slug,
                          titleDetailedExplainerSlug: help.detailedSlug,
                          onTitleDetailedExplainerClick: (explainerSlug: string) => trackDetailedExplainerOpen(explainerSlug, 'list'),
                      }
                    : {}),
            };
        };

        const predictedSpeciesRenderer = (params: TableCellRendererParams, tab: 'iucn' | 'invasives') => {
            const cell = params.cellData as TableCellData;
            if (typeof cell === 'number') {
                return <span>{cell}</span>;
            }
            if (cell && typeof cell === 'object' && 'data' in cell && cell.data !== null) {
                const { count, siteId } = cell.data as PredictedSpeciesColumnMetadata;
                return (
                    <button
                        type='button'
                        className='text-primary underline cursor-pointer'
                        onClick={() => handleOpenModal({ tab, siteId })}
                    >
                        {count}
                    </button>
                );
            }
            return null;
        };

        const habitatColumns: DataTableColumns = LIST_TAB_HABITAT_METRIC_COLUMNS.map(metricKey => ({
            columnId: metricKey,
            ...columnWithOptionalHeaderHelp(metricKey, listTabMetricColumnLabel(metricKey)),
            width: '9%',
            isSortable: true,
            renderer: numericCellRenderer(HABITAT_METRIC_CELL_CLASS),
        }));

        return [
            {
                columnId: 'siteName',
                title: listHeaderTitle('Site Name'),
                width: '200px',
                isSortable: true,
            },
            {
                columnId: 'area',
                title: listHeaderTitle('Area (km²)'),
                isSortable: true,
                renderer: numericCellRenderer(AREA_CELL_CLASS),
            },
            {
                columnId: 'country',
                title: listHeaderTitle('Country'),
                isSortable: true,
            },
            ...habitatColumns,
            {
                columnId: 'predictedRedList',
                ...columnWithOptionalHeaderHelp('POSSIBLE_IUCN_RED_LIST_SPECIES', 'Predicted IUCN Red List species'),
                width: '15%',
                isSortable: true,
                sortFn: speciesSortFn,
                renderer: (params: TableCellRendererParams) => predictedSpeciesRenderer(params, 'iucn'),
            },
            {
                columnId: 'predictedInvasive',
                ...columnWithOptionalHeaderHelp('POSSIBLE_INVASIVE_SPECIES', 'Predicted invasive species'),
                width: '15%',
                isSortable: true,
                sortFn: speciesSortFn,
                renderer: (params: TableCellRendererParams) => predictedSpeciesRenderer(params, 'invasives'),
            },
            {
                columnId: 'siteLevelData',
                title: listHeaderTitle('Site level data'),
                isSortable: false,
                width: '80px',
            },
        ];
    }, [trackDetailedExplainerOpen]);

    const handleStateChange = (newState: Partial<DataTableState>) => {
        setTableState(newState);
        // In client-side mode DataTable owns pagination/sort — don't update API params.
        if (!isServerSideSort) return;

        if (newState.currentPageNumber) {
            goToPage(newState.currentPageNumber);
        }
        if (newState.sortedColumn) {
            const { columnId, sortedDirection } = newState.sortedColumn;
            const backendField = columnIdToSortField[columnId];
            if (backendField) {
                const newOrder = toApiSortOrder(sortedDirection);
                if (backendField !== sortState.field || newOrder !== sortState.order) {
                    setSortState({ field: backendField, order: newOrder });
                    goToPage(1);
                }
            }
        }
    };

    return (
        <div className={styles.listViewContainer}>
            <div className={styles.siteStats}>
                <span className={styles.siteStatsText}>
                    Showing {displayedSitesCount} of {totalItems} sites
                </span>
            </div>
            <DataTable
                state={tableState}
                columns={columns}
                onStateChange={handleStateChange}
                onSort={trackColumnSortClick}
                emptyStateMessage={isFetching ? 'Loading...' : 'No results'}
                className='rounded-xl flex-1 overflow-hidden flex flex-col'
                allowSortClear={false}
                totalRowsForPagination={totalItems}
                isServerSideSort={isServerSideSort}
                isLoadingOverlay={isFetching && siteTableRows.length > 0}
            />

            {isModalOpen && modalSiteId && (
                <SiteSpeciesRiskModal onClose={() => setModalOpen(false)} siteId={modalSiteId} initialTab={modalTab} />
            )}
        </div>
    );
};

export default PortfolioSiteExplorerListView;
